import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { NotificationDevice } from '../../../../Models/Devices/NotificationDevice';
import { SortableList } from '../../../SortableList';
import { serviceBundler, webSocketService } from '../../../../App';

import style from './NotificationDeviceList.module.css';
import { useTranslation } from 'react-i18next';

import { BellOutlined, WifiOutlined, LoadingOutlined } from '@ant-design/icons';
import EditNotificationDeviceLay from '../../EditNotificationDeviceLay/EditNotificationDeviceLay';
import { MessageWebSocket } from '../../../../Models/WebSocket/MessageWebSocket';
import { MessageSock } from '../../../../Models/WebSocket/MessageSock';
import { WebSocketMessageTypeIncoming } from '../../../../Enums/WebSocketMessageTypeIncoming';
import { WsEspStatus } from '../../../../Models/WebSocket/Incoming/WsEspStatus';
import { WsLogosStatus } from '../../../../Models/WebSocket/Incoming/WsLogosStatus';
import { Transput } from '../../../../Models/Platform/Transput';
import { WsDevicesStatus } from '../../../../Models/WebSocket/Incoming/WsDevicesStatus';

const NotificationDeviceList = forwardRef(
  (props: { isLoading: boolean; isEdit: Function }, ref) => {
    const { t, i18n } = useTranslation();

    const [editDevice, setEditDevice] = useState(false);
    const [externalEditDevice, setExternalEditDevice] = useState(false);

    const [loading, setLoading] = useState(true);

    const [notificationDevices, setNotificationDevices] = useState<
      NotificationDevice[]
    >([]);
    const [notificationDevice, setNotificationDevice] =
      useState<NotificationDevice>();
    const [transputs, setTransputs] = useState<Transput[]>();
    const [loadingWifi, setLoadingWifi] = useState(true);

    useImperativeHandle(ref, () => ({
      AddDevice() {
        var newDevice = new NotificationDevice();
        setNotificationDevice(newDevice);
        setEditDevice(true);
      },

      SetExternalEdit(value: boolean) {
        if (!editDevice) {
          setExternalEditDevice(value);
        }
      },
    }));

    useEffect(() => {
      if (loading) {
        FetchItems();
        FetchItemsTransput();
      }
    }, [loading]);

    useEffect(() => {
      if (editDevice) {
        props.isEdit(true);
      }
    }, [editDevice]);

    useEffect(() => {
      WatchData();
    }, [0]);

    const WatchData = async () => {
      serviceBundler.devicesService.GetRefreshStatus().subscribe((value) => {
        if (!value) {
          return;
        }

        var fetchedNotificationDevices =
          serviceBundler.devicesService.GetNotificationDevices();

        setNotificationDevices([...fetchedNotificationDevices]);
      });
    };

    const FetchItems = async () => {
      serviceBundler.devicesService.GetFetchStatus().subscribe((value) => {
        if (!value) {
          return;
        }

        var fetchedNotificationDevices =
          serviceBundler.devicesService.GetNotificationDevices();
        if (fetchedNotificationDevices !== undefined) {
          setNotificationDevices(fetchedNotificationDevices);
        }

        SetWifiStatus();
        setLoading(false);
      });
    };

    const FetchItemsTransput = async () => {
      serviceBundler.platformService.GetFetchStatus().subscribe((value) => {
        if (!value) {
          return;
        }

        var fetchedTransputs = serviceBundler.platformService.GetTransputs();
        if (fetchedTransputs !== undefined) {
          setTransputs(fetchedTransputs);
        }

        SetWifiStatus();
        setLoading(false);
      });
    };

    const ChangeOrderDevices = (items: NotificationDevice[]) => {
      var reorderedItems =
        serviceBundler.devicesService.ReplaceNotificationDevices(items);
      setNotificationDevices(reorderedItems);
    };

    const ShowLoadingForWifi = (isOnline: boolean): boolean => {
      if (loadingWifi && !isOnline) {
        return true;
      }
      return false;
    };

    const IsOfline = (isOnline: boolean): boolean => {
      if (!loadingWifi && !isOnline) {
        return true;
      }
      return false;
    };

    const SetWifiStatus = () => {
      if (!serviceBundler.devicesService.GetWifiStatus()) {
        setInterval(() => {
          setLoadingWifi(false);
          serviceBundler.devicesService.SetWifiStatus();
        }, 1000);
      } else {
        setLoadingWifi(false);
      }
    };

    const EditDevice = (item: NotificationDevice) => {
      setEditDevice(true);
      setNotificationDevice(item);
    };

    const RemoveDevice = (item: NotificationDevice) => {
      var index = notificationDevices.findIndex((x) => x.id == item.id);
      if (index >= 0) {
        notificationDevices.splice(index, 1);
        setNotificationDevices(notificationDevices);
      }
      setEditDevice(false);
      props.isEdit(false);
    };

    const SafeDeviceWithoutClose = (item: NotificationDevice) => {
      SafeDevice(item);
    };

    const SafeDeviceAndClose = (item: NotificationDevice) => {
      SafeDevice(item);
      setEditDevice(false);
      props.isEdit(false);
    };

    const SafeDevice = (item: NotificationDevice) => {
      setNotificationDevice(item);
      var index = notificationDevices.findIndex((x) => x.id == item.id);
      notificationDevices[index] = item;
      setNotificationDevices(notificationDevices);
    };

    const CancelEditDevice = (item: NotificationDevice) => {
      if (item.id == '') {
        var index = notificationDevices.findIndex((x) => x.id == item.id);
        if (index >= 0) {
          notificationDevices.splice(index, 1);
          setNotificationDevices(notificationDevices);
        }
      }

      setEditDevice(false);
      props.isEdit(false);
    };

    return (
      <>
        {!externalEditDevice && (
          <>
            {editDevice && (
              <EditNotificationDeviceLay
                allData={notificationDevices}
                data={notificationDevice}
                dataTransputs={transputs ? transputs : []}
                onSafe={SafeDeviceAndClose}
                onSafeWithoutClose={SafeDeviceWithoutClose}
                onRemove={RemoveDevice}
                onCancel={CancelEditDevice}
              ></EditNotificationDeviceLay>
            )}

            {!editDevice && (
              <SortableList
                items={notificationDevices ? notificationDevices : []}
                onChange={ChangeOrderDevices}
                renderItem={(item) => (
                  <SortableList.Item id={item.id}>
                    <div
                      className={style.stylingRow}
                      onClick={() => EditDevice(item)}
                    >
                      <div className={style.styleIcon}>
                        <BellOutlined style={{ fontSize: 20 }} />
                      </div>
                      <div className={style.styleName}>{item.moduleName}</div>
                      {item.online && (
                        <div className={style.styleWifi}>
                          <WifiOutlined style={{ fontSize: 20 }} />
                        </div>
                      )}
                      {IsOfline(item.online) && (
                        <>
                          <div className={style.styleWifi}>
                            <div className={style.diagonalLine}></div>
                            <WifiOutlined style={{ fontSize: 20 }} />
                          </div>
                        </>
                      )}
                      {ShowLoadingForWifi(item.online) && (
                        <div className={style.styleWifi}>
                          <LoadingOutlined style={{ fontSize: 20 }} />
                        </div>
                      )}
                    </div>
                    <SortableList.DragHandle />
                  </SortableList.Item>
                )}
              />
            )}
          </>
        )}
      </>
    );
  },
);

export default NotificationDeviceList;
